import {FlexBox} from 'Components/FlexBox';
import {Button, Input, message, Table, TableColumnsType} from 'antd';
import useRequest from 'Utils/Hooks/useRequest';
import {LinkButton} from 'Features/Buttons/LinkButton';
import useSelection from 'Utils/Hooks/useSelection';
import React, {useState} from 'react';
import {ExportOutlined, PlusOutlined, UploadOutlined} from '@ant-design/icons';
import useModalHandler from 'Utils/Hooks/useModalHandler';
import {RoleAssignImport} from '../RoleAssignImport';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import {useConfirmDelete} from '../../../../../Utils/Hooks/useConfirmDelete';
import {AddUserModal} from '../AddUserModal';
import {errorMessage} from '../../../../../Utils/common';
import {
    deleteUserRole,
    exportRoleUserExcel,
    getRoleUserList,
    RoleUserItem,
} from '../../service';
import useAuth from '../../../../../Utils/Hooks/useAuth';

interface RoleAssignProps {
    roleId: string;
}

export function RoleAssign({roleId}: RoleAssignProps) {
    const auth = useAuth();
    const selection = useSelection();
    const assignImportModal = useModalHandler({component: RoleAssignImport});
    const addUserModal = useModalHandler({component: AddUserModal});

    const [searchText, setSearchText] = useState('');

    const [data, {loading}, refresh] = useRequest(
        async () => {
            selection.setSelected([]);
            const data = await getRoleUserList(roleId);

            if (!searchText) return data;
            return data.filter(
                item =>
                    item.userName.includes(searchText) ||
                    item.userCode.includes(searchText),
            );
        },
        {},
        [roleId, searchText],
    );

    const [download, downloadStatus] = useRequestFn(async () => {
        try {
            await exportRoleUserExcel();
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    const onDelete = useConfirmDelete(
        async (userId: string) => {
            await deleteUserRole([userId], roleId);
            refresh();
        },
        {
            confirmMessage:
                '该用户的授权被取消后将不能使用该角色拥有权限的功能，确定取消授权？',
        },
    );

    const onDeleteMultiple = useConfirmDelete(
        async () => {
            await deleteUserRole(selection.selected, roleId);
            refresh();
        },
        {
            confirmMessage:
                '该用户的授权被取消后将不能使用该角色拥有权限的功能，确定取消授权？',
        },
    );

    function onImportOk() {
        refresh();
        assignImportModal.close();
    }

    function onAddOk() {
        refresh();
        addUserModal.close();
    }

    const columns: TableColumnsType<RoleUserItem> = [
        {title: '序号', render: (_, _item, index) => index + 1},
        {title: '用户姓名', dataIndex: 'userName'},
        {title: '用户账号', dataIndex: 'userCode'},
        {
            title: '状态',
            dataIndex: 'userStatus',
            render: userStatus => (userStatus === '1' ? '正常' : '冻结'),
        },
        {
            title: '操作',
            align: 'center',
            render: (_, item) => (
                <LinkButton onClick={() => onDelete(item.id)}>
                    取消授权
                </LinkButton>
            ),
        },
    ];

    return (
        <FlexBox
            flexDirection="column"
            alignItems="stretch"
            pointerEvents={auth('SF_J476Zfxw39') ? 'auto' : 'none'}>
            <FlexBox gap="10px" width="100%" marginBottom="20px">
                <Button
                    type="primary"
                    onClick={() => addUserModal.open({roleId, onOk: onAddOk})}>
                    <PlusOutlined /> 添加授权人员
                </Button>

                <Button
                    type="primary"
                    ghost
                    icon={<UploadOutlined />}
                    onClick={() => assignImportModal.open({onOk: onImportOk})}>
                    导入授权人员
                </Button>

                <Button
                    icon={<ExportOutlined />}
                    onClick={download}
                    loading={downloadStatus.loading}>
                    导出授权人员
                </Button>

                <Button
                    disabled={selection.selected.length === 0}
                    onClick={() => onDeleteMultiple()}>
                    批量取消授权
                </Button>

                <FlexBox marginLeft="auto" width="300px">
                    <Input.Search
                        placeholder="输入用户名或用户账号后按回车搜索"
                        onSearch={setSearchText}
                    />
                </FlexBox>
            </FlexBox>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={data || []}
                loading={loading}
                rowSelection={selection}
                pagination={false}
            />

            {assignImportModal.render()}
            {addUserModal.render()}
        </FlexBox>
    );
}
